import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as exchangeApi from '@/api/exchanges';

/**
 * Query keys for exchange-related queries
 */
export const exchangeKeys = {
  all: ['exchanges'] as const,
  available: () => [...exchangeKeys.all, 'available'] as const,
  userExchanges: (userId: string, includeBalances: boolean) =>
    [...exchangeKeys.all, 'user', userId, { includeBalances }] as const,
};

/**
 * Hook to get all available exchanges
 */
export const useAvailableExchanges = () => {
  return useQuery({
    queryKey: exchangeKeys.available(),
    queryFn: () => exchangeApi.getAvailableExchanges(),
  });
};

/**
 * Hook to get user's configured exchanges
 */
const THIRTY_MINUTES_MS = 30 * 60 * 1000;
const BALANCE_CACHE_BUFFER_MS = 5 * 60 * 1000;

const PENDING_BALANCE_REFETCH_MS = 6 * 1000;

export const useUserExchanges = (userId: string | undefined, includeBalances: boolean = false) => {
  const shouldThrottle = includeBalances;

  return useQuery({
    queryKey: exchangeKeys.userExchanges(userId || '', includeBalances),
    queryFn: () => exchangeApi.getUserExchanges(userId!, includeBalances),
    enabled: !!userId,
    staleTime: shouldThrottle ? THIRTY_MINUTES_MS : undefined,
    gcTime: shouldThrottle ? THIRTY_MINUTES_MS + BALANCE_CACHE_BUFFER_MS : undefined,
    refetchOnWindowFocus: shouldThrottle ? false : undefined,
    refetchOnReconnect: shouldThrottle ? false : undefined,
    refetchOnMount: shouldThrottle ? false : undefined,
    refetchInterval: shouldThrottle
      ? (query) =>
          query.state.data?.some((exchange) => exchange.balance?.pending)
            ? PENDING_BALANCE_REFETCH_MS
            : false
      : undefined,
  });
};

interface SetupExchangeParams {
  userId: string;
  exchangeCode: string;
  apiKey: string;
  apiSecret: string;
  apiPassphrase?: string;
}

/**
 * Hook to setup exchange credentials
 */
export const useSetupExchange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SetupExchangeParams) =>
      exchangeApi.setupExchange(
        params.userId,
        params.exchangeCode,
        params.apiKey,
        params.apiSecret,
        params.apiPassphrase
      ),
    onSuccess: (_data, variables) => {
      // Invalidate user exchanges after setup
      queryClient.invalidateQueries({
        queryKey: exchangeKeys.userExchanges(variables.userId, false),
      });
      queryClient.invalidateQueries({
        queryKey: exchangeKeys.userExchanges(variables.userId, true),
      });
    },
  });
};

interface ReorderExchangesParams {
  userId: string;
  exchangeCodes: string[];
}

/**
 * Hook to persist Connected Exchanges drag order (autotrade priority).
 */
export const useReorderExchanges = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ReorderExchangesParams) =>
      exchangeApi.reorderExchanges(params.userId, params.exchangeCodes),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: exchangeKeys.userExchanges(variables.userId, false),
      });
      queryClient.invalidateQueries({
        queryKey: exchangeKeys.userExchanges(variables.userId, true),
      });
    },
  });
};

interface RemoveExchangeParams {
  userId: string;
  exchangeCode: string;
}

/**
 * Hook to remove exchange for user
 */
export const useRemoveExchange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: RemoveExchangeParams) =>
      exchangeApi.removeExchange(params.userId, params.exchangeCode),
    onSuccess: (_data, variables) => {
      // Invalidate user exchanges after removal
      queryClient.invalidateQueries({
        queryKey: exchangeKeys.userExchanges(variables.userId, false),
      });
      queryClient.invalidateQueries({
        queryKey: exchangeKeys.userExchanges(variables.userId, true),
      });
    },
  });
};

interface RefreshBalanceParams {
  userId: string;
  exchangeCode: string;
}

/**
 * Hook to manually force-refresh the balance for a single exchange.
 * The server enforces a 30-minute throttle per user/exchange.
 * On success, the userExchanges query (with balances) is invalidated so
 * the UI reflects the freshly fetched data.
 *
 * Check `isBalanceRefreshThrottledApiError(error)` on mutation failure
 * to distinguish throttle errors from other failures.
 */
export const useRefreshBalance = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Awaited<ReturnType<typeof exchangeApi.refreshBalance>>,
    Error,
    RefreshBalanceParams
  >({
    mutationFn: (params: RefreshBalanceParams) =>
      exchangeApi.refreshBalance(params.userId, params.exchangeCode),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: exchangeKeys.userExchanges(variables.userId, true),
      });
    },
  });
};


